import React, { useMemo, useState, useCallback } from "react";
import {
  TableContainer,
  Box,
  Button,
  OutlinedInput,
  Table,
  Typography,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Container } from "@mui/system";
import { styled } from "@mui/material/styles";
import { ArrowBack, LanguageOutlined } from "@mui/icons-material";
import { InfoRounded } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import PaginationFooter from "./components/paginationFooter";

/**
 * Styled TableRow with custom border styles
 */
const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
  borderRadius: "10px",
  background: "white",
  marginBottom: "10px",
  "& td": {
    borderTop: "1px solid rgba(224, 224, 229, 1)",
    borderBottom: "1px solid rgba(224, 224, 229, 1)",
    borderRight: "none",
  },
  "& td:first-of-type": {
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
    borderLeft: "1px solid rgba(224, 224, 229, 1)",
    borderRight: "none",
  },
  "& td:last-child": {
    borderTopRightRadius: "10px",
    borderBottomRightRadius: "10px",
    borderRight: "1px solid rgba(224, 224, 229, 1)",
    borderLeft: "none",
  },
}));

/**
 * Styled TableRow for header with column dividers
 */
const StyledHeaderRow = styled(TableRow)(() => ({
  "& .MuiTableCell-root": {
    backgroundColor: "#E9E9EC",
    borderBottom: "1px solid #E9E9EC",
    color: "#61616F",
  },
  "& .MuiTableCell-root::after": {
    content: '""',
    position: "absolute",
    top: 8,
    right: 0,
    bottom: 18,
    borderRight: "2px solid #CECED3",
    height: "50%",
    backgroundColor: "#CECED3",
  },
  "& .MuiTableCell-root:last-child::after": {
    content: "none",
  },
}));

/**
 * Scrollbar styles for TableContainer
 */
const scrollBarSx = {
  "&::-webkit-scrollbar": {
    width: "14px",
    height: "14px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "60px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#E4E4E5",
    borderRadius: "10px",
    border: "4px solid rgba(0, 0, 0, 0)",
    backgroundClip: "padding-box",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#d5d5d5",
  },
};

// Internal imports
import { CustomDropdown } from "../orderStatus/statusList";
import { useOrderDetails } from "./hooks/useOrderDetails";
import {
  STATUS_LIST,
  STATUS_LIST_WITHOUT_ALL,
} from "./constants/orderConstants";
import { TableLoadingState, TableEmptyState } from "./components/tableStates";

const PROVIDER_CONFIG = {
  google: { label: "Google", icon: "/socialicon.png" },
  microsoft: { label: "Microsoft", icon: "/microsofticon.png" },
};

/**
 * Domain contact row component
 */
const DomainContactRow = ({
  domain,
  contact,
  forwardDomain,
  isSelected,
  onToggleSelect,
  onStatusUpdate,
}) => {
  const statusInfo = useMemo(
    () =>
      STATUS_LIST.find(
        (s) => s.value?.toLowerCase() === contact.status?.toLowerCase()
      ),
    [contact.status]
  );

  const formattedDate = useMemo(() => {
    const date = domain.createdAt || domain.startDate;
    return date ? moment(date).format("DD-MM-YYYY") : "-";
  }, [domain.createdAt, domain.startDate]);

  return (
    <StyledTableRow>
      {/* Domain with checkbox */}
      <TableCell align="left" sx={{ borderBottom: "none", cursor: "default" }}>
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
          <Checkbox checked={isSelected} onChange={() => onToggleSelect(contact)} />
          <LanguageOutlined
            sx={{ color: "rgba(88, 98, 255, 1)", marginRight: "10px" }}
          />
          {domain.domain}
        </Box>
      </TableCell>

      {/* Provider */}
      <TableCell align="left">
        {domain.provider && (
          <Typography
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              borderRadius: "10px",
              p: "6px 12px",
              border: "1px solid #E0E0E5",
              width: "fit-content",
            }}
          >
            {PROVIDER_CONFIG[domain.provider]?.icon && (
              <img
                src={PROVIDER_CONFIG[domain.provider].icon}
                alt={PROVIDER_CONFIG[domain.provider]?.label || domain.provider}
              />
            )}
            <Typography sx={{ fontSize: "0.875rem", fontWeight: "500" }}>
              {PROVIDER_CONFIG[domain.provider]?.label || domain.provider}
            </Typography>
          </Typography>
        )}
      </TableCell>

      {/* Email */}
      <TableCell align="left">{contact.email}</TableCell>

      {/* Purchase Date */}
      <TableCell align="left">{formattedDate}</TableCell>

      {/* Forward Domain */}
      <TableCell align="left">{forwardDomain ?? "-"}</TableCell>

      {/* Status Dropdown */}
      <TableCell align="left">
        {statusInfo && (
          <CustomDropdown
            label={statusInfo.label}
            statusIcon={statusInfo.icon}
            options={STATUS_LIST_WITHOUT_ALL}
            selected={contact.status}
            handleSelect={(status) => onStatusUpdate(status, contact)}
          />
        )}
      </TableCell>

      {/* Extra Domains */}
      <TableCell align="left">
        <Tooltip
          title={
            domain.extraDomains?.length > 0 ? (
              <div style={{ padding: "10px" }}>
                <Typography color="inherit">Extra Domains:</Typography>
                <br />
                {domain.extraDomains.map((ed, idx) => (
                  <Typography key={idx} color="inherit">
                    {ed}
                  </Typography>
                ))}
              </div>
            ) : (
              "No Extra Domains Found"
            )
          }
        >
          <IconButton>
            <InfoRounded />
          </IconButton>
        </Tooltip>
      </TableCell>
    </StyledTableRow>
  );
};

/**
 * Table header for DFY Status section
 */
const DFYTableHeader = ({ allContacts, selectedContacts, onSelectAll }) => (
  <TableHead sx={{ background: "#E9E9EC", borderRadius: "10px" }}>
    <StyledHeaderRow>
      <TableCell align="left">
        <Checkbox
          checked={allContacts.length > 0 && selectedContacts.length === allContacts.length}
          indeterminate={
            selectedContacts.length > 0 && selectedContacts.length < allContacts.length
          }
          onChange={onSelectAll}
          disabled={allContacts.length === 0}
        />
        Name
      </TableCell>
      <TableCell align="left">Provider</TableCell>
      <TableCell align="left">Emails</TableCell>
      <TableCell align="left">Purchase Date</TableCell>
      <TableCell align="left">Forward Domain</TableCell>
      <TableCell align="left">Status</TableCell>
      <TableCell align="left">
        Extra Domains
        <Tooltip title="Extra domains are selected by users as fallback options to be used in case the primary chosen domains are unavailable during the purchase process. Priority is according to list position (first = highest).">
          <IconButton size="small">
            <InfoRounded />
          </IconButton>
        </Tooltip>
      </TableCell>
    </StyledHeaderRow>
  </TableHead>
);

/**
 * DFY Status Section Component
 * Shows detailed order status with contact management
 * Now works as a standalone page with URL params
 */
const DFYStatusSection = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const {
    orders,
    totalCount,
    isLoading,
    searchText,
    status,
    page,
    rowsPerPage,
    allContacts,
    selectedContacts,
    selectedCount,
    handleSearchChange,
    handleStatusChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleToggleContact,
    handleSelectAll,
    isContactSelected,
    updateOrderStatus,
    updateBulkOrderStatus,
    reset,
  } = useOrderDetails({
    orderId,
  });

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    status: null,
    contact: null,
    isBulk: false,
  });

  const handleStatusUpdateRequest = useCallback((status, contact) => {
    setConfirmDialog({ open: true, status, contact, isBulk: false });
  }, []);

  const handleBulkStatusUpdateRequest = useCallback((status) => {
    setConfirmDialog({ open: true, status, contact: null, isBulk: true });
  }, []);

  const handleConfirmStatusUpdate = useCallback(() => {
    const { status, contact, isBulk } = confirmDialog;
    if (isBulk) {
      updateBulkOrderStatus(status);
    } else {
      updateOrderStatus(status, contact);
    }
    setConfirmDialog({ open: false, status: null, contact: null, isBulk: false });
  }, [confirmDialog, updateOrderStatus, updateBulkOrderStatus]);

  const handleCancelStatusUpdate = useCallback(() => {
    setConfirmDialog({ open: false, status: null, contact: null, isBulk: false });
  }, []);

  // Handle close/back - navigate back to orders list
  const handleClose = () => {
    reset();
    navigate("/manage/whiteLabel-DFY");
  };

  // Render table content
  const renderTableContent = () => {
    if (isLoading) {
      return <TableLoadingState colSpan={7} />;
    }

    if (!orders?.domains?.length) {
      return <TableEmptyState colSpan={7} />;
    }

    return (
      <TableBody>
        {orders.domains.flatMap((domain, domainIndex) =>
          domain.contactDetails?.map((contact, contactIndex) => (
            <DomainContactRow
              key={`${domainIndex}-${contactIndex}-${contact._id}`}
              domain={domain}
              forwardDomain={orders.forwardDomain}
              contact={contact}
              isSelected={isContactSelected(contact)}
              onToggleSelect={handleToggleContact}
              onStatusUpdate={handleStatusUpdateRequest}
            />
          ))
        )}
      </TableBody>
    );
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 2,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl" sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
        <Tooltip title="Back to Orders">
          <IconButton
            onClick={handleClose}
            sx={{
              border: "1px solid #E9E9EC",
              borderRadius: "12px",
              padding: "4px 8px",
              marginRight: "10px",
            }}
          >
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h4">
            Order Status
          </Typography>
          {orders?.user?.email && (
            <Typography variant="body2" sx={{ color: "#61616F", mt: 0.5 }}>
              User: <strong>{orders?.user?.email}</strong>
              {orders?.partnerEmail && (
                <span> &nbsp;|&nbsp; Partner: <strong>{orders.partnerEmail}</strong></span>
              )}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Filters and Bulk Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        {/* Search and Filter */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <OutlinedInput
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchText}
          />
          <CustomDropdown
            label={status === "" ? "All Status" : status}
            options={STATUS_LIST}
            selected={status}
            handleSelect={(value) => handleStatusChange(value)}
          />
          <Button variant="outlined" onClick={handleClose}>
            Back
          </Button>
        </Box>

        {/* Bulk Actions */}
        {selectedCount > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
              Selected: {selectedCount}
            </Typography>
            <CustomDropdown
              label="Select Status"
              options={STATUS_LIST_WITHOUT_ALL}
              selected={status}
              handleSelect={handleBulkStatusUpdateRequest}
            />
          </Box>
        )}
      </Box>

      {/* Scrollable Table Container */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          ...scrollBarSx,
        }}
      >
        <TableContainer sx={{
          borderRadius: "0px",
          overflow: "auto",
          maxHeight: "100%",
          maxWidth: "100%",
        }}>
          <Table
            sx={{
              height: "fit-content",
              borderCollapse: "separate",
              borderSpacing: "0px 15px !important",
              minWidth: "max-content",
            }}
            aria-label="Order details table"
            stickyHeader
          >
            <DFYTableHeader
              allContacts={allContacts}
              selectedContacts={selectedContacts}
              onSelectAll={handleSelectAll}
            />
            {renderTableContent()}
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      <PaginationFooter
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
        isTotalDomain={true}
      />
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancelStatusUpdate}>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.isBulk
              ? `Are you sure you want to update the status of ${selectedCount} selected order(s) to "${confirmDialog.status}"?`
              : `Are you sure you want to update the status to "${confirmDialog.status}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStatusUpdate}>Cancel</Button>
          <Button onClick={handleConfirmStatusUpdate} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DFYStatusSection;